import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead, absoluteImageUrl } from "@/lib/seo";
import halibutImg from "@/assets/halibut-catch.jpeg.asset.json";
import boatImg from "@/assets/boat-hewescraft.png.asset.json";

// og:image and the FAQPage JSON-LD `image` must both resolve to a valid
// absolute URL — crawlers reject relative paths and reject non-image types
// (previously we were pointing og:image at the hero .mp4). `halibutImg` is a
// real JPEG asset served from the Lovable CDN; `absoluteImageUrl` promotes
// the pointer's site-relative `/__l5e/…` URL to an absolute https:// URL —
// use it at every image call site so no route can drift out of sync with
// SITE_ORIGIN.
const FISHING_OG_IMAGE_ABS = absoluteImageUrl(halibutImg);
const FISHING_OG_IMAGE_ALT =
  "Guest holding a halibut caught in Clarence Strait near Coffman Cove, Alaska";

// Fact-check flags for Anthony before publish:

//  - Confirm the "$3,000+ per person" guided-lodge comparison figure.
//  - Confirm ferry / floatplane routing details from Ketchikan.
//  - Confirm on-site freezer and fish-cleaning station availability.

const TITLE =
  "Self-Guided Fishing on Prince of Wales Island, Alaska | Coffman Cove Lodging";
const DESCRIPTION =
  "Self-guided salmon and halibut fishing out of Coffman Cove, Alaska. Rent a boat, stay in a cabin, and fish Clarence Strait on your own schedule. Live fishing report included.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "How many halibut can I keep?",
    a: "Fishing self-guided out of Coffman Cove, every angler may keep 2 halibut per day — any size, no annual limit. On a Southeast Alaska (Area 2C) charter boat in 2026, you're restricted to a reverse slot size limit and can't keep halibut at all on Thursdays from June 18 through September 10. Source: NOAA Fisheries 2026 halibut regulations for unguided and charter anglers.",
  },
  {
    q: "Do I need a fishing license?",
    a: "Yes. Every angler needs an Alaska sport fishing license, and a king salmon stamp if you plan to keep kings. Both are available online through the Alaska Department of Fish & Game before you arrive.",
  },
  {
    q: "Do I need boating experience?",
    a: "You should be comfortable operating a powerboat and reading a tide table. Self-guided means self-guided — we'll orient you to the local water, but you're the captain.",
  },
  {
    q: "When is the best time to come?",
    a: "July through early September is the peak window: silvers running hard, halibut steady, and the most daylight of the year. June is your best shot at kings.",
  },
  {
    q: "How do I get my fish home?",
    a: "Freeze your catch on-site, pack it in insulated fish boxes, and check it as luggage on your flight home. Most guests fly home with 50–100 lbs of fillets.",
  },
];

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://coffmancovelodging.com/fishing#faq",
  url: "https://coffmancovelodging.com/fishing",
  // Absolute image URL — required by schema.org validators. Reuses the same
  // artwork as og:image so the share preview and the structured-data image
  // stay in lockstep.
  image: FISHING_OG_IMAGE_ABS,
  inLanguage: "en-US",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

// BreadcrumbList JSON-LD is emitted globally by the <Breadcrumbs /> component
// on every non-home route — no per-route duplicate needed here.


export const Route = createFileRoute("/fishing")({
  head: () =>
    seoHead({
      path: "/fishing",
      title: TITLE,
      description: DESCRIPTION,
      image: FISHING_OG_IMAGE_ABS,
      imageAlt: FISHING_OG_IMAGE_ALT,
      imageType: "image/jpeg",
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(FAQ_JSON_LD),
        },
      ],

    }),
  component: FishingPage,
});


function FishingPage() {
  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 py-16">
      <header>
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
          Prince of Wales Island · Southeast Alaska
        </p>
        <h1 className="font-display text-4xl md:text-5xl mt-3 text-balance leading-[1.1]">
          Self-Guided Fishing on Prince of Wales Island
        </h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          Coffman Cove sits on the northeast shore of Prince of Wales Island,
          facing Clarence Strait — one of the most productive stretches of
          salmon and halibut water in Southeast Alaska. No guide, no schedule,
          no per-day charter bill. You rent the boat, you keep the fish, and
          you fish when the tide says to fish.
        </p>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          That's how we've done it since 1984. Guided lodges on the island run
          $3,000+ per person for a week. Self-guided out of Coffman Cove, you
          keep control of the trip and most of that money.
        </p>
      </header>

      <figure className="my-10 overflow-hidden rounded-lg border border-border">
        <img
          src={halibutImg.url}
          alt="Guest holding a halibut caught in Clarence Strait near Coffman Cove"
          loading="lazy"
          className="w-full object-cover"
        />
      </figure>

      <section className="mt-12">
        <h2 className="font-display text-3xl">What You'll Catch</h2>
        <div className="mt-6 space-y-6 text-base leading-relaxed">
          <p>
            <strong>King (Chinook) Salmon</strong> — Kings move through
            Clarence Strait from late spring into July. Trolling herring or
            spoons along the tide rips near the cove is the standard program.
          </p>
          <p>
            <strong>Coho (Silver) Salmon</strong> — The main event from
            mid-July through September. Aggressive, abundant, and close to
            town — many of our guests limit out within a few miles of the
            dock.
          </p>
          <p>
            <strong>Halibut</strong> — Flat, ugly, and the best-eating fish in
            the ocean. Productive halibut ground sits within a short run of
            the cove. Anchor up on a slack tide with herring or jigs.
          </p>
          <p>
            <strong>Rockfish &amp; Lingcod</strong> — Structure fishing along
            the kelp lines and reefs produces yelloweye, black bass, and
            lingcod. Great fallback on days the salmon are moody.
          </p>
          <p>
            <strong>Steelhead, Dolly Varden &amp; Cutthroat</strong> — Prince
            of Wales has more than a thousand miles of road and dozens of
            fishable rivers and creeks. Bring waders and a light rod; some of
            the best freshwater fishing in Alaska is a short drive from your
            cabin.
          </p>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="font-display text-3xl">How Self-Guided Works Here</h2>
        <ol className="mt-6 space-y-4 list-decimal pl-6 text-base leading-relaxed">
          <li>
            <strong>Book your week.</strong> Our rentals run Friday to Friday —
            cabin and boat together, one booking. See{" "}
            <Link
              to="/accommodations"
              className="text-[var(--color-ember)] underline underline-offset-4 hover:text-[var(--color-ember)]"
            >
              Accommodations
            </Link>{" "}
            for cabins and rates.
          </li>
          <li>
            <strong>Get here.</strong> Fly into Ketchikan, then take the ferry
            or a floatplane to Prince of Wales. Coffman Cove is a paved-road
            drive from the ferry terminal.
          </li>
          <li>
            <strong>Fish.</strong> We'll point you at what's biting — and so
            will our{" "}
            <Link
              to="/fishing-report"
              className="text-[var(--color-ember)] underline underline-offset-4 hover:text-[var(--color-ember)]"
            >
              live fishing report
            </Link>
            , updated with current conditions in the cove. You won't fish
            blind on day one.
          </li>
          <li>
            <strong>Process your catch.</strong> Clean your fish at the
            station, freeze it, and fly your fish boxes home.
          </li>
        </ol>
      </section>

      <figure className="my-12 overflow-hidden rounded-lg border border-border">
        <img
          src={boatImg.url}
          alt="Hewescraft rental boat rigged for self-guided fishing out of Coffman Cove"
          loading="lazy"
          className="w-full object-cover"
        />
      </figure>

      <section className="mt-4">
        <h2 className="font-display text-3xl">Why Coffman Cove</h2>
        <p className="mt-6 text-base leading-relaxed">
          Most of the island's fishing traffic launches out of Craig and
          Klawock on the west side. Coffman Cove faces the east side —
          Clarence Strait — which means less pressure on the water and a
          shorter run to fish. The town is small, quiet, and built around
          fishing. This isn't a resort. It's a working Alaska town where the
          fishing happens to be world-class.
        </p>
      </section>

      {/* Halibut advantage — self-guided vs charter (2026 NOAA Area 2C regs). */}
      <section
        aria-labelledby="halibut-advantage"
        className="mt-14 rounded-xl border-2 border-[var(--color-ember)] bg-card p-8 md:p-10 shadow-sm"
      >
        <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
          2026 Halibut Regulations
        </p>
        <h2
          id="halibut-advantage"
          className="font-display text-3xl md:text-4xl mt-3 text-balance leading-[1.15]"
        >
          Keep More Fish. It's the Law.
        </h2>
        <p className="mt-5 text-base md:text-lg leading-relaxed">
          Fishing self-guided out of Coffman Cove, every angler may keep{" "}
          <strong>2 halibut per day — any size, no annual limit</strong>. On a
          Southeast Alaska charter boat in 2026, you're restricted to a reverse
          slot size limit and can't keep halibut at all on Thursdays from
          June 18 through September 10.
        </p>
        <p className="mt-4 text-base md:text-lg leading-relaxed font-semibold">
          Self-guided isn't just cheaper. You go home with more fish.
        </p>
        <p className="mt-6 text-xs text-muted-foreground">
          Source:{" "}
          <a
            href="https://www.fisheries.noaa.gov/alaska/resources-fishing/sport-halibut-fishing-alaska"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-[var(--color-ember)]/60 underline-offset-2 hover:text-[var(--color-ember)]"
          >
            NOAA Fisheries 2026 halibut regulations for unguided and charter
            anglers, Area 2C (Southeast Alaska)
          </a>
          .
        </p>
      </section>


      <section className="mt-14">
        <h2 className="font-display text-3xl">Frequently Asked Questions</h2>
        <dl className="mt-6 space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <dt className="font-semibold text-lg">{f.q}</dt>
              <dd className="mt-2 text-base text-muted-foreground leading-relaxed">
                {f.a}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-16 rounded-lg border border-border bg-[var(--color-parchment)]/40 p-8 text-center">
        <h2 className="font-display text-3xl">Book Your Week</h2>
        <p className="mt-3 text-base text-muted-foreground">
          Cabins and boats rent Friday to Friday — depart Friday morning, arrive
          Friday midday or evening. Peak weeks go early — check availability now.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-md bg-[var(--color-ember-deep)] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:opacity-90"
          >
            Book Now
          </Link>
          <Link
            to="/accommodations"
            className="rounded-md border border-border px-5 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-background"
          >
            View Accommodations
          </Link>
        </div>
      </section>
    </article>
  );
}
